import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { IconType } from "react-icons";
import {
  MdCode,
  MdLogout,
  MdHistory,
  MdAutoAwesome,
  MdClose,
  MdOutlineSpaceDashboard,
  MdOutlineChat,
  MdOutlineCampaign,
  MdOutlineContacts,
  MdOutlineWorkspacePremium,
  MdOutlinePhotoLibrary,
  MdOutlineAccountCircle,
  MdOutlineMenuBook,
  MdOutlineReceiptLong,
  MdOutlineGroups,
  MdOutlineVpnKey,
} from "react-icons/md";
import { FaWhatsapp } from "react-icons/fa";
import { FaMeta } from "react-icons/fa6";
import { TbBroadcast, TbTemplate, TbRobot } from "react-icons/tb";
import { IoWalletOutline } from "react-icons/io5";

import { profileIcon } from "../../asset";
import logoLight from "../../asset/images/empedance-light.svg";
import { logout } from "../../features/user/UserSlice";
import { getAuthUser, isTeamAdmin } from "../../util/manageAuth";
import { resetWallet } from "../../features/wallet/WalletSlice";
import { useSidebar } from "../layouts/SidebarContext";
import { SiShopify, SiZoho } from "react-icons/si";

/* ===================== MENU ITEMS ===================== */

export interface NavItem {
  name: string;
  Icon: IconType;
  navLink: string;
  section: string;
  activePaths?: string[];
  /** Owner and org admins only (hidden from agents). */
  adminOnly?: boolean;
}

export const getNavItems = (role: string | undefined): NavItem[] => allNavItems(role).filter(
  (item) => !item.adminOnly || isTeamAdmin(),
);

const allNavItems = (role: string | undefined): NavItem[] => [
  { section: "Overview", name: "Dashboard", Icon: MdOutlineSpaceDashboard, navLink: "/admin/dashboard" },
  { section: "Overview", name: "Live Chat", Icon: MdOutlineChat, navLink: "/admin/live-chat" },

  {
    section: "Messaging",
    name: "Campaign",
    Icon: MdOutlineCampaign,
    navLink: "/admin/variables-send-message/bulk",
    activePaths: ["/admin/variables-send-message"],
  },
  { section: "Messaging", name: "BroadCasts", Icon: TbBroadcast, navLink: "/admin/broadcasts" },
  { section: "Messaging", name: "Templates", Icon: TbTemplate, navLink: "/admin/variables-templates" },
  { section: "Messaging", name: "History", Icon: MdHistory, navLink: "/admin/reports" },

  { section: "Audience & Content", name: "Studio", Icon: MdAutoAwesome, navLink: "/admin/studio" },
  { section: "Audience & Content", name: "Gallery", Icon: MdOutlinePhotoLibrary, navLink: "/admin/gallery" },
  { section: "Audience & Content", name: "Contacts", Icon: MdOutlineContacts, navLink: "/admin/customers" },
  { section: "Audience & Content", name: "Train Bot", Icon: TbRobot, navLink: "/admin/chatbot" },

  { section: "WhatsApp", name: "Meta Profile", Icon: FaMeta, navLink: "/admin/meta-profile", adminOnly: true },
  { section: "WhatsApp", name: "WhatsApp Onboarding", Icon: FaWhatsapp, navLink: "/admin/whatsapp/connect", adminOnly: true },
  { section: "WhatsApp", name: "WhatsApp Billing", Icon: MdOutlineReceiptLong, navLink: "/admin/whatsapp/billing", adminOnly: true },

  { section: "Account", name: "Team", Icon: MdOutlineGroups, navLink: "/admin/team", adminOnly: true },
  { section: "Account", name: "Subscription", Icon: MdOutlineWorkspacePremium, navLink: "/admin/pricing", adminOnly: true },
  { section: "Account", name: "Wallet", Icon: IoWalletOutline, navLink: "/admin/wallet", adminOnly: true },
  { section: "Account", name: "Profile", Icon: MdOutlineAccountCircle, navLink: "/admin/profile" },

  { section: "Developers", name: "API Keys", Icon: MdOutlineVpnKey, navLink: "/admin/developer/api", adminOnly: true },
  { section: "Developers", name: "Developer", Icon: MdCode, navLink: "/admin/developer/company", adminOnly: true },
  { section: "Developers", name: "Api Documentation", Icon: MdOutlineMenuBook, navLink: "/admin/api-documentation" },

  
  { section: "Integrations", name: "Shopify", Icon: SiShopify, navLink: "/admin/integrations/shopify", adminOnly: true },
  { section: "Integrations", name: "Zoho CRM", Icon: SiZoho, navLink: "/admin/integrations/zoho", adminOnly: true },
];

// Keep navItems export for backward compatibility with Navbar.tsx
export const navItems = getNavItems(getAuthUser()?.role);

/* ===================== SIDEBAR ===================== */

export default function Sidebar() {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { open, setOpen } = useSidebar();

  // Called inside component so it re-evaluates on each render
  const user = getAuthUser();
  const navItems = getNavItems(user?.role);

  // Group items by section, keeping their order
  const sections = navItems.reduce<{ title: string; items: NavItem[] }[]>((acc, item) => {
    const last = acc[acc.length - 1];
    if (last && last.title === item.section) last.items.push(item);
    else acc.push({ title: item.section, items: [item] });
    return acc;
  }, []);

  const handleLogout = () => {
    dispatch(resetWallet());
    dispatch(logout("Logged out successfully"));
    navigate("/login");
  };

  return (
    <>
      {/* Mobile backdrop */}
      <div
        onClick={() => setOpen(false)}
        className={`lg:hidden fixed inset-0 z-40 bg-slate-950/50 backdrop-blur-[2px] transition-opacity duration-200 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      />

      <aside
        className={`fixed left-0 top-0 z-50 h-screen w-64 flex flex-col overflow-hidden bg-[#0b1220] text-slate-300 transition-transform duration-200 ease-out lg:translate-x-0 ${
          open ? "translate-x-0 shadow-pop" : "-translate-x-full"
        }`}
      >
        {/* Ambient glow */}
        <div className="pointer-events-none absolute -top-24 -left-16 w-72 h-72 rounded-full bg-brand-main/25 blur-3xl" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-px bg-gradient-to-b from-white/10 via-white/[0.04] to-transparent" />

        {/* Logo */}
        <div className="relative h-[72px] flex-shrink-0 flex items-center justify-between px-6">
          <img src={logoLight} alt="Empedance" className="h-9 w-auto object-contain" />
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close menu"
            className="lg:hidden w-9 h-9 rounded-xl flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/[0.06]"
          >
            <MdClose size={20} />
          </button>
        </div>

        {/* MENU */}
        <nav className="app-scroll-dark relative flex-1 min-h-0 overflow-y-auto px-3 pb-4">
          {sections.map((section) => (
            <div key={section.title} className="mt-5 first:mt-1">
              <p className="px-3 mb-2 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                {section.title}
              </p>

              {section.items.map(({ name, Icon, navLink, activePaths }) => {
                const isActive = activePaths
                  ? activePaths.some((path) => pathname.startsWith(path))
                  : pathname.startsWith(navLink);

                return (
                  <NavLink
                    key={name}
                    to={navLink}
                    aria-current={isActive ? "page" : undefined}
                    className={`group relative flex items-center gap-3 h-10 px-3 my-[3px] rounded-xl text-[13.5px] transition-all duration-150 ${
                      isActive
                        ? "bg-gradient-to-r from-white/[0.11] to-white/[0.04] text-white font-semibold shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]"
                        : "text-slate-400 font-medium hover:bg-white/[0.05] hover:text-slate-100"
                    }`}
                  >
                    {isActive && (
                      <span className="absolute -left-3 top-2.5 bottom-2.5 w-[3px] rounded-r-full bg-sky-400 shadow-[0_0_12px_rgba(56,189,248,0.8)]" />
                    )}
                    <Icon
                      className={`w-[18px] h-[18px] flex-shrink-0 transition-colors ${
                        isActive ? "text-sky-400" : "text-slate-500 group-hover:text-slate-300"
                      }`}
                    />
                    <span className="truncate">{name}</span>
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        {/* User + logout */}
        <div className="relative flex-shrink-0 p-3">
          <div className="flex items-center gap-3 p-2.5 rounded-2xl bg-white/[0.04] ring-1 ring-inset ring-white/[0.06]">
            <img
              src={user?.profilePic || profileIcon}
              alt=""
              className="w-9 h-9 rounded-full object-cover flex-shrink-0 bg-white ring-2 ring-white/10"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src = profileIcon;
              }}
            />
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold text-white truncate">{user?.userName || "User"}</p>
              <p className="text-[11.5px] text-slate-400 truncate">{user?.email}</p>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              title="Logout"
              aria-label="Logout"
              className="w-9 h-9 flex-shrink-0 rounded-xl flex items-center justify-center text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition"
            >
              <MdLogout size={18} />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
